import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as ProductApi from "@src/api/ProductApi";
import type {
  Manufacturer,
  ProductCategory,
  Species,
} from "@src/types/product";

function asListPayload<T>(body: unknown): T[] {
  if (Array.isArray(body)) {
    return body as T[];
  }
  if (
    body &&
    typeof body === "object" &&
    Array.isArray((body as { results?: unknown }).results)
  ) {
    return (body as { results: T[] }).results;
  }
  return [];
}

type MegaMenuFailures = {
  categories?: boolean;
  manufacturers?: boolean;
  species?: boolean;
};

type ShopMegaMenuState = {
  productCategories: ProductCategory[];
  manufacturers: Manufacturer[];
  species: Species[];
  loading: boolean;
  loaded: boolean;
  failures: MegaMenuFailures;
  allEndpointsFailed: boolean;
  fetchMegaMenuData: () => Promise<void>;
};

let fetchInFlight: Promise<void> | null = null;

const initialState = {
  productCategories: [] as ProductCategory[],
  manufacturers: [] as Manufacturer[],
  species: [] as Species[],
  loading: false,
  loaded: false,
  failures: {} as MegaMenuFailures,
  allEndpointsFailed: false,
};

export const useShopMegaMenuStore = create<ShopMegaMenuState>()(
  devtools((set, get) => ({
    ...initialState,

    fetchMegaMenuData: async () => {
      if (get().loaded) {
        return;
      }
      if (fetchInFlight) {
        return fetchInFlight;
      }

      fetchInFlight = (async () => {
        set(
          {
            loading: true,
            failures: {},
            allEndpointsFailed: false,
          },
          false,
          "shop-mega-menu/fetch-start",
        );

        const results = await Promise.allSettled([
          ProductApi.getProductCategories(),
          ProductApi.getManufacturers(),
          ProductApi.getSpecies(),
        ]);

        const failures: MegaMenuFailures = {};

        let productCategories = get().productCategories;
        const catResult = results[0];
        if (catResult.status === "fulfilled") {
          productCategories = asListPayload<ProductCategory>(
            catResult.value.body,
          );
        } else {
          console.error(catResult.reason);
          failures.categories = true;
        }

        let manufacturers = get().manufacturers;
        const manResult = results[1];
        if (manResult.status === "fulfilled") {
          manufacturers = asListPayload<Manufacturer>(manResult.value.body);
        } else {
          console.error(manResult.reason);
          failures.manufacturers = true;
        }

        let species = get().species;
        const speciesResult = results[2];
        if (speciesResult.status === "fulfilled") {
          species = asListPayload<Species>(speciesResult.value.body);
        } else {
          console.error(speciesResult.reason);
          failures.species = true;
        }

        const anyFulfilled = results.some((r) => r.status === "fulfilled");
        const allEndpointsFailed =
          results[0].status === "rejected" &&
          results[1].status === "rejected" &&
          results[2].status === "rejected";

        set(
          {
            productCategories,
            manufacturers,
            species,
            failures,
            allEndpointsFailed,
            loading: false,
            loaded: anyFulfilled,
          },
          false,
          "shop-mega-menu/fetch-done",
        );
      })();

      try {
        await fetchInFlight;
      } finally {
        fetchInFlight = null;
      }
    },
  })),
);

export function resetShopMegaMenuStoreForTests(): void {
  fetchInFlight = null;
  useShopMegaMenuStore.setState({ ...initialState });
}
